/**
 * Labstep
 *
 * @module components/ExperimentWorkflow/Filter/Status/Select
 * @desc Select filter for status
 */

import { isActive } from 'labstep-web/components/Filter/List/utils';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { Select as ReactSelect } from 'labstep-web/core/Select';
import { SearchSelectContainer as SearchSelect } from 'labstep-web/core/Select/Search';
import { useHasFeatureFlagEnabled } from 'labstep-web/hooks/feature-flag';
import { EntityState } from 'labstep-web/models/entity-state.model';
import { StatusTypes } from 'labstep-web/models/experiment-workflow.model';
import type React from 'react';
import type { ExperimentWorkflowFilterStatusSelectProps } from './types';

export const ExperimentWorkflowFilterStatusSelect: React.FC<
  ExperimentWorkflowFilterStatusSelectProps
> = ({ setParams, searchParams, activeGroup }) => {
  const activeStatusType = Object.values(StatusTypes).find((value) =>
    isActive(searchParams, value.params),
  );
  const hasSignatureFeatureEnabled = useHasFeatureFlagEnabled(
    'signature_workflow',
  );
  return (
    <Modal
      header={'Status'}
      content={({ toggleModal }) => (
        <Flex column spacing="gap">
          <ReactSelect
            value={
              activeStatusType
                ? {
                    label: activeStatusType.text,
                    value: activeStatusType.params,
                  }
                : null
            }
            placeholder={
              hasSignatureFeatureEnabled
                ? 'Select status type'
                : 'Select status'
            }
            options={Object.values(StatusTypes).map((value) => ({
              label: value.text,
              value: value.params,
            }))}
            isClearable
            onChange={(selectedOption) => {
              setParams(
                selectedOption
                  ? selectedOption.value
                  : Object.keys(StatusTypes.completed.params).reduce(
                      (result, key) => ({
                        ...result,
                        [key]: undefined,
                      }),
                      {},
                    ),
              );
              toggleModal();
            }}
          />
          {hasSignatureFeatureEnabled && (
            <>
              OR
              <SearchSelect
                isCursor
                entityName={EntityState.entityName}
                params={{
                  group_id: activeGroup?.id,
                  group_by_name: 1,
                  sort: 'name',
                }}
                onChange={(selectedOption) => {
                  setParams({
                    entity_state_name: selectedOption
                      ? selectedOption.name
                      : undefined,
                  });
                  toggleModal();
                }}
                value={null}
                placeholder="Select a specific status"
              />
            </>
          )}
        </Flex>
      )}
      viewComponent={({ toggleModal }) => (
        <ActionComponent
          type="option"
          icon={StatusTypes.completed.icon}
          text={'Status'}
          onClick={toggleModal}
        />
      )}
    />
  );
};

export default withActiveGroup(ExperimentWorkflowFilterStatusSelect);
